import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { studentApi } from '../../api/services';

const BulkUpload = ({ type, onUpload, onCancel }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errors, setErrors] = useState([]);

  const getTemplateInfo = () => {
    switch (type) {
      case 'students':
        return {
          title: 'Bulk Upload Students',
          description: `Upload an Excel file (XLSX) with student information. ${
            localStorage.getItem('organizationType')?.toUpperCase() === 'COLLEGE'
              ? 'Mobile Number is REQUIRED.'
              : 'Mobile Number is optional.'
          }`,
          // Backend provides the template, headers determined by backend
          templateHeaders: [], 
          sampleData: []
        };
      case 'parents':
        return {
          title: 'Bulk Upload Parents',
          description: 'Upload a CSV file with parent information',
          templateHeaders: ['name', 'phoneNumber', 'relation', 'email'],
          sampleData: [
            ['Jane Doe', '+91 98765 43210', 'Mother', 'jane.doe@email.com'],
            ['Bob Smith', '+91 98765 43211', 'Father', 'bob.smith@email.com']
          ]
        };
      case 'results':
        return {
          title: 'Bulk Upload Results',
          description: 'Upload a CSV file with exam results',
          templateHeaders: ['studentRegistrationId', 'subjectName', 'examType', 'totalMarks', 'obtainedMarks', 'examDate'],
          sampleData: [
            ['REG001', 'Mathematics', 'UNIT_TEST', '100', '85', '2024-01-15'],
            ['REG002', 'Science', 'MID_TERM', '100', '92', '2024-01-20']
          ]
        };
      case 'attendance':
        return {
          title: 'Bulk Upload Attendance',
          description: 'Upload a CSV file with attendance records',
          templateHeaders: ['studentRegistrationId', 'date', 'status'],
          sampleData: [
            ['REG001', '2024-01-15', 'present'],
            ['REG002', '2024-01-15', 'absent']
          ]
        };
      default:
        return {
          title: 'Bulk Upload',
          description: 'Upload a CSV file',
          templateHeaders: [],
          sampleData: []
        };
    }
  };

  const templateInfo = getTemplateInfo();

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    // Allow XLSX and CSV files
    const isValidFile = selectedFile && (
      selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      selectedFile.name.endsWith('.xlsx') ||
      selectedFile.type === 'text/csv' ||
      selectedFile.name.endsWith('.csv')
    );
    
    if (isValidFile) {
      setFile(selectedFile);
      setErrors([]);
      setUploadResult(null);
    } else {
      setErrors(['Please select a valid Excel file (.xlsx) or CSV file']);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrors(['Please select a file to upload']);
      return;
    }

    setUploading(true);
    setErrors([]);
    setUploadResult(null);

    try {
      let response;
      if (type === 'students') {
        response = await studentApi.bulkUploadStudents(file);
      } else {
        // Fallback for other types (Mock behavior)
        await new Promise(resolve => setTimeout(resolve, 2000));
        response = {
            success: true,
            data: {
                totalRecords: 150,
                successfulRecords: 150,
                failedRecords: 0,
                errors: []
            }
        };
      }

      if (response.success) {
        setUploadResult({
          success: true,
          totalRecords: response.data?.totalRecords || response.totalRecords || 0,
          successfulRecords: response.data?.successfulRecords || response.successfulRecords || 0,
          failedRecords: response.data?.failedRecords || response.failedRecords || 0,
          errors: response.data?.errors || response.errors || []
        });
        if (onUpload) {
            // Short delay to let user see success message before closing (if parent chooses to close)
             setTimeout(() => onUpload(response.data), 1500);
        }
      } else {
        throw new Error(response.message || 'Upload failed');
      }

    } catch (error) {
      setUploadResult({
        success: false,
        error: error.message || 'Upload failed. Please try again.'
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    if (type === 'students') {
        try {
            const blob = await studentApi.getBulkUploadSample();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'student_bulk_upload_template.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrors(['Failed to download template. Please try again.']);
        }
        return;
    }

    // Default existing client-side generation for other types
    const csvContent = [
      templateInfo.templateHeaders.join(','),
      ...templateInfo.sampleData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${type}_template.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{templateInfo.title}</h2>
            <p className="text-sm text-gray-600">{templateInfo.description}</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Template Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-800">Download Template</h3>
                <p className="text-sm text-blue-600 mt-1">
                  Download the Excel template (XLSX) with the correct format and sample data
                </p>
                <button
                  onClick={downloadTemplate}
                  className="mt-2 flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <Download className="h-4 w-4" />
                  <span className="text-sm font-medium">Download {type === 'students' ? 'student_template.xlsx' : `${type}_template.csv`}</span>
                </button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File (XLSX or CSV)
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <div className="space-y-2">
                <p className="text-sm text-gray-600">
                  Drag and drop your Excel (XLSX) or CSV file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="btn-primary cursor-pointer inline-flex items-center space-x-2"
                >
                  <Upload className="h-4 w-4" />
                  <span>Choose File</span>
                </label>
              </div>
              {file && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-green-800">{file.name}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Please fix the following errors:</h3>
                  <ul className="mt-2 text-sm text-red-600 space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className={`border rounded-lg p-4 ${
              uploadResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                {uploadResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1">
                  {uploadResult.success ? (
                    <>
                      <h3 className="text-sm font-medium text-green-800">Upload Successful!</h3>
                      <div className="mt-2 text-sm text-green-600 space-y-1">
                        <p>• Total records: {uploadResult.totalRecords}</p>
                        <p>• Successfully imported: {uploadResult.successfulRecords}</p>
                        <p>• Failed records: {uploadResult.failedRecords}</p>
                      </div>
                      {uploadResult.errors && uploadResult.errors.length > 0 && (
                        <div className="mt-3">
                          <h4 className="text-sm font-medium text-orange-800">Failed Records:</h4>
                          <ul className="mt-1 text-sm text-orange-600 space-y-1">
                            {uploadResult.errors.map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Upload Failed</h3>
                      <p className="mt-1 text-sm text-red-600">{uploadResult.error}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  <span>Upload File</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

BulkUpload.propTypes = {
  type: PropTypes.string.isRequired,
  onUpload: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
};

export default BulkUpload;
